import routes from '@/routes/routes';

/**
 * Título da página e anúncio de troca de tela.
 *
 * Dois problemas que só aparecem em SPA e que este serviço resolve:
 *
 * 1. TÍTULO (WCAG 2.4.2 — Page Titled). O `<title>` vinha fixo do public/index.html
 *    ("Consulte Informática - SAEWEB") e nunca mudava. Consequências práticas: o leitor
 *    de tela anuncia o mesmo nome em toda tela, a lista de abas do navegador fica com
 *    dezenas de abas idênticas, e o histórico não diz onde o usuário esteve.
 *
 * 2. ANÚNCIO DA NAVEGAÇÃO. Numa aplicação de página única a troca de tela não recarrega
 *    nada, então o leitor de tela não tem por que falar: o usuário clica no menu e o
 *    silêncio é o mesmo de um clique que não funcionou. A região viva alimentada aqui é
 *    lida a cada navegação.
 *
 * Este serviço NÃO move o foco de propósito. Mover o foco para o conteúdo a cada
 * navegação é um padrão comum, mas neste sistema há rotas abertas por modal e pelo
 * proxy do legado, e o salto atrapalharia mais do que ajuda. Quem navega por teclado
 * chega ao conteúdo pelo atalho "Pular para o conteúdo principal" (MainTemplate).
 */

const NOME_DO_SISTEMA = 'SAEWEB - Consulte Informática';

/**
 * Descrição da tela a partir do nome da rota.
 *
 * A busca reproduz a hierarquia real da tabela de rotas: as telas são netas de `/home`
 * (`home` → módulo → tela) e as Homes de aluno/professor/responsável são rotas de
 * primeiro nível com nomes curtos. O campo `description` é uma propriedade própria do
 * projeto no registro de rota (não fica em `meta`), então tem de ser lido do array
 * importado — o objeto que o Vue Router entrega em `afterEach` não carrega campos
 * desconhecidos.
 */
function descricaoDaRota(nomeRota: string | symbol | null | undefined): string {
  if (!nomeRota) return '';

  const nome = String(nomeRota);

  const homesPorAtalho: Record<string, string> = {
    homep: 'homeprofessor',
    homea: 'homealuno',
    homer: 'homeresponsavel',
  };

  const nomeEfetivo = homesPorAtalho[nome] ?? nome;

  const noPrimeiroNivel = routes.find((rota: any) => rota.name === nomeEfetivo);
  if (noPrimeiroNivel) return (noPrimeiroNivel as any).description ?? '';

  const home: any = routes.find((rota: any) => rota.name === 'home');

  for (const modulo of home?.children ?? []) {
    if (modulo.name === nome) return modulo.description ?? '';

    const tela = modulo.children?.find((filha: any) => filha.name === nome);
    if (tela) return tela.description ?? '';
  }

  return '';
}

/** A região viva mora no App.vue e é criada uma única vez, junto com a aplicação. */
function regiaoDeAnuncio(): HTMLElement | null {
  return document.getElementById('anuncio-navegacao');
}

export default {
  descricaoDaRota,

  /**
   * Anuncia uma mensagem no leitor de tela usando a região viva permanente do App.vue.
   *
   * Use isto para retorno de ação que NÃO tem representação visual própria (ou cuja
   * representação o usuário pode não perceber): "12 registros encontrados",
   * "Filtro aplicado", "Item removido da lista".
   *
   * Por que existe, em vez de cada tela criar a própria região: o leitor de tela só
   * observa mudanças em regiões que JÁ estavam no DOM. Uma `<div aria-live>` criada
   * junto com a mensagem (o padrão `v-if="mensagem"`) costuma não produzir anúncio
   * nenhum — é o erro mais comum de mensagem dinâmica, e nenhuma ferramenta automática
   * o detecta. Aqui a região é única, permanente, e só o texto dela muda.
   *
   * `role="alert"` é a exceção conhecida: esse é anunciado ao ser inserido no DOM, então
   * uma mensagem de erro que aparece depois de uma ação pode continuar usando
   * `<div role="alert" v-if="erro">` normalmente.
   *
   * @param mensagem Texto a ser lido. Vazio limpa a região.
   */
  anunciar(mensagem: string) {
    const regiao = regiaoDeAnuncio();
    if (!regiao) return;

    // Reatribuir o MESMO texto não dispara anúncio: a região viva só reage a mudança de
    // conteúdo. Limpar antes garante que a repetição também seja lida.
    regiao.textContent = '';
    window.setTimeout(() => {
      const atual = regiaoDeAnuncio();
      if (atual) atual.textContent = mensagem;
    }, 100);
  },

  /**
   * Chamado a cada navegação concluída.
   *
   * `aplicarNomenclatura` é injetado por quem chama para o título respeitar os nomes
   * configuráveis por escola/ano (PDI, ficha descritiva) — o serviço de nomenclatura
   * não é importado aqui para não criar dependência circular com as rotas.
   */
  anunciarTela(
    nomeRota: string | symbol | null | undefined,
    aplicarNomenclatura: (texto: string) => string = (texto) => texto,
  ) {
    const descricao = aplicarNomenclatura(descricaoDaRota(nomeRota));

    document.title = descricao ? `${descricao} — ${NOME_DO_SISTEMA}` : NOME_DO_SISTEMA;

    const regiao = regiaoDeAnuncio();
    if (!regiao || !descricao) return;

    /*
     * Reatribuir o MESMO texto não dispara anúncio (a região viva só reage a mudança de
     * conteúdo), e navegar duas vezes para a mesma tela é comum aqui — pelo menu e pelos
     * favoritos. Limpar antes garante que a segunda vez também seja lida.
     *
     * O `setTimeout` dá ao navegador a chance de processar a limpeza como uma mudança
     * separada; sem ele, limpar e reescrever no mesmo tique é visto como uma alteração só.
     */
    regiao.textContent = '';
    window.setTimeout(() => {
      const atual = regiaoDeAnuncio();
      if (atual) atual.textContent = `${descricao}. Tela carregada.`;
    }, 100);
  },
};
